import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import CreatorEditor from '../components/editor/CreatorEditor';
import {
  Upload,
  Film,
  Image as ImageIcon,
  Video,
  Sparkles,
  Camera,
  Send,
  Save,
  ChevronDown,
} from 'lucide-react';

export default function CreatorEditorPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState(null); // image, video
  const [showEditor, setShowEditor] = useState(false);
  const [editedDataUrl, setEditedDataUrl] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishType, setPublishType] = useState('post'); // post, story, reel
  const [caption, setCaption] = useState('');

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      toast.error('Please select an image or video file');
      return;
    }

    setMediaFile(file);
    setMediaType(isVideo ? 'video' : 'image');
    setMediaUrl(URL.createObjectURL(file));
    setShowEditor(true);
    setEditedDataUrl(null);
  };

  const handleEditorSave = (dataUrl) => {
    setEditedDataUrl(dataUrl);
    setShowEditor(false);
    toast.success('Edits applied!');
  };

  const handleEditorCancel = () => {
    setShowEditor(false);
  };

  const handlePublish = async () => {
    if (!editedDataUrl && !mediaUrl) {
      toast.error('No media to publish');
      return;
    }

    setPublishing(true);
    try {
      // Convert data URL to blob
      const sourceUrl = editedDataUrl || mediaUrl;
      let blob;

      if (sourceUrl.startsWith('data:')) {
        const res = await fetch(sourceUrl);
        blob = await res.blob();
      } else {
        blob = mediaFile;
      }

      const formData = new FormData();
      formData.append('file', blob, `edited_${Date.now()}.png`);
      formData.append('caption', caption);
      formData.append('publish_type', publishType);

      const uploadRes = await fetch(`${API}/editor/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (uploadRes.ok) {
        const result = await uploadRes.json();
        toast.success(`Published to ${publishType}!`);
        navigate(result.redirect_url || '/feed');
      } else {
        const err = await uploadRes.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to publish');
      }
    } catch {
      toast.error('Connection error');
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveDraft = async () => {
    const sourceUrl = editedDataUrl || mediaUrl;
    if (!sourceUrl) return;

    try {
      let blob;
      if (sourceUrl.startsWith('data:')) {
        const res = await fetch(sourceUrl);
        blob = await res.blob();
      } else { blob = mediaFile; }

      const formData = new FormData();
      formData.append('file', blob, `draft_${Date.now()}.png`);
      formData.append('caption', caption);

      const res = await fetch(`${API}/editor/draft`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        toast.success('Draft saved!');
      } else {
        toast.error('Failed to save draft');
      }
    } catch { toast.error('Connection error'); }
  };

  // Show editor
  if (showEditor && mediaUrl) {
    return <CreatorEditor mediaUrl={mediaUrl} mediaType={mediaType} onSave={handleEditorSave} onCancel={handleEditorCancel} />;
  }

  // Show publish screen (after editing)
  if (editedDataUrl) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4" data-testid="publish-screen">
        <h1 className="text-xl font-bold">Ready to Publish</h1>

        {/* Preview */}
        <div className="relative rounded-xl overflow-hidden border border-border bg-card">
          <img src={editedDataUrl} alt="Edited" className="w-full" />
          <Button variant="outline" size="sm" onClick={() => setShowEditor(true)}
            className="absolute bottom-3 right-3 bg-background/50 backdrop-blur-sm border-border"
            data-testid="re-edit-btn">
            <Sparkles className="w-3 h-3 mr-1" /> Edit More
          </Button>
        </div>

        {/* Caption */}
        <textarea value={caption} onChange={e => setCaption(e.target.value)}
          placeholder="Write a caption..."
          className="w-full h-20 bg-muted/30 border border-border rounded-xl p-3 text-sm resize-none focus:border-violet-500 focus:outline-none"
          data-testid="publish-caption" />

        {/* Publish type */}
        <div className="flex gap-2">
          {[
            { v: 'post', l: 'Feed Post', i: ImageIcon },
            { v: 'story', l: 'Story', i: Camera },
            { v: 'reel', l: 'Reel', i: Film },
          ].map(t => (
            <button key={t.v} onClick={() => setPublishType(t.v)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl border text-sm transition-all ${
                publishType === t.v ? 'border-violet-500 bg-violet-500/10 text-violet-300' : 'border-border text-muted-foreground/80'
              }`}
              data-testid={`publish-${t.v}`}>
              <t.i className="w-4 h-4" /> {t.l}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSaveDraft} className="flex-1 gap-1" data-testid="save-draft-btn">
            <Save className="w-4 h-4" /> Save Draft
          </Button>
          <Button onClick={handlePublish} disabled={publishing} className="flex-1 bg-violet-600 hover:bg-violet-700 gap-1" data-testid="publish-btn">
            {publishing ? 'Publishing...' : <><Send className="w-4 h-4" /> Publish</>}
          </Button>
        </div>
      </div>
    );
  }

  // Upload screen (initial)
  return (
    <div className="max-w-lg mx-auto px-4 py-8" data-testid="editor-upload-screen">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold mb-1">Creator Editor</h1>
        <p className="text-sm text-muted-foreground">Edit your videos, stories, and reels with text, emoji, stickers, filters, and more.</p>
      </div>

      <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFileSelect} className="hidden" />

      <div className="space-y-3">
        <Card className="p-6 border-dashed border-border bg-muted/30 text-center cursor-pointer hover:border-violet-500/30 transition-colors"
          onClick={() => fileRef.current?.click()} data-testid="upload-area">
          <Upload className="w-10 h-10 mx-auto mb-3 text-violet-400" />
          <p className="font-medium mb-1">Upload Media</p>
          <p className="text-xs text-muted-foreground">Images or videos. Max 100MB.</p>
        </Card>

        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Photo', icon: ImageIcon, accept: 'image/*' },
            { label: 'Video', icon: Video, accept: 'video/*' },
            { label: 'Camera', icon: Camera, accept: 'image/*;capture=camera' },
          ].map((opt, i) => (
            <Button key={i} variant="outline" className="h-14 flex-col gap-1"
              onClick={() => { fileRef.current.accept = opt.accept; fileRef.current.click(); }}>
              <opt.icon className="w-5 h-5" />
              <span className="text-xs">{opt.label}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
